import { KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react'
import { Draggable } from 'react-smooth-dnd'
import { arrayMoveImmutable } from 'array-move'
import Close from '@mui/icons-material/Close'
import DragIndicator from '@mui/icons-material/DragIndicator'
import GroupOutlined from '@mui/icons-material/GroupOutlined'
import {
  Box,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import { PolicyPositionOption } from '@/hooks/usePolicyPositionOptions'
import useUsersById from '@/hooks/useUsersById'
import { ApprovalPolicyApprover } from '@/types/approvals'
import {
  ApproverRef,
  approverRefKey,
  orderedApproverRefs,
  toApproverEntries,
} from '@/utils/approver-entries'
import PositionAvatar from '@/components/avatars/PositionAvatar'
import UserAvatar from '@/components/avatars/UserAvatar'
import { DraggableList } from '@/components/lists/DraggableList'
import ApproverSearchInput from './ApproverSearchInput'

type ApproverListProps = {
  /** Approvers in the policy's nested write shape. Order comes from `sort_order`. */
  value: ApprovalPolicyApprover[]
  /** Called with a densely renumbered list on every add, remove, or reorder. */
  onChange: (approvers: ApprovalPolicyApprover[]) => void
  /**
   * Every position this project has — see `usePolicyPositionOptions`. Used to
   * label a saved row regardless of `canAddPositions`, so a position a policy
   * already asks for is never shown as unknown.
   */
  positionOptions: PolicyPositionOption[]
  /** False keeps positions out of the picker without hiding saved ones. */
  canAddPositions?: boolean
  positionsPending?: boolean
  disabled?: boolean
  /** Order isn't meaningful on any-order policies. */
  showNumbers?: boolean
}

function EmptyState() {
  return (
    <Stack
      alignItems="center"
      spacing={0.5}
      sx={{ textAlign: 'center', px: 2, py: 3 }}
    >
      <GroupOutlined sx={{ fontSize: 32, color: 'text.disabled' }} />
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        No approvers yet
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Search above to add the first approver.
      </Typography>
    </Stack>
  )
}

export default function ApproverList({
  value,
  onChange,
  positionOptions,
  canAddPositions = true,
  positionsPending = false,
  disabled = false,
  showNumbers = true,
}: ApproverListProps) {
  const savedRefs = useMemo(() => orderedApproverRefs(value), [value])

  const savedUserIds = useMemo(
    () => savedRefs.flatMap((ref) => (ref.kind === 'user' ? [ref.userId] : [])),
    [savedRefs],
  )

  // Only needed to put names on existing rows — the picker runs the same
  // query itself and shares this one's cache.
  const { usersById, isPending: usersPending } = useUsersById({
    enabled: savedUserIds.length > 0,
  })

  const positionOptionsById = useMemo(
    () =>
      new Map(positionOptions.map((option) => [option.position.id, option])),
    [positionOptions],
  )

  const containerRef = useRef<HTMLDivElement>(null)
  const [pendingFocusIndex, setPendingFocusIndex] = useState<number | null>(
    null,
  )

  // A saved approver from another account is dropped once the users query
  // has loaded. A position is never dropped: the server refuses to delete one
  // a policy names, so a missing one means this project's catalog is still
  // loading or was read under a different filter.
  const refs = useMemo(
    () =>
      usersPending
        ? savedRefs
        : savedRefs.filter(
            (ref) => ref.kind === 'position' || usersById.has(ref.userId),
          ),
    [savedRefs, usersById, usersPending],
  )

  const emit = (nextRefs: ApproverRef[]) => {
    onChange(toApproverEntries(nextRefs))
  }

  const addApprover = (ref: ApproverRef) => {
    const key = approverRefKey(ref)
    if (refs.some((existing) => approverRefKey(existing) === key)) return
    emit([...refs, ref])
  }

  const removeApprover = (key: string) => {
    emit(refs.filter((ref) => approverRefKey(ref) !== key))
  }

  const moveApprover = (from: number, to: number) => {
    if (to < 0 || to >= refs.length) return
    emit(arrayMoveImmutable(refs, from, to))
  }

  // react-smooth-dnd is pointer-only, so the handle doubles as a keyboard
  // control.
  const handleKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return
    // Swallow the arrow key even at the ends of the list, so a no-op move
    // doesn't scroll the page instead.
    event.preventDefault()
    const to = event.key === 'ArrowUp' ? index - 1 : index + 1
    if (to < 0 || to >= refs.length) return
    moveApprover(index, to)
    setPendingFocusIndex(to)
  }

  useEffect(() => {
    if (pendingFocusIndex === null) return
    containerRef.current
      ?.querySelector<HTMLButtonElement>(
        `[data-approver-handle="${pendingFocusIndex}"]`,
      )
      ?.focus()
    setPendingFocusIndex(null)
  }, [pendingFocusIndex])

  return (
    <Stack spacing={2} ref={containerRef}>
      <Stack direction="row" justifyContent="flex-end">
        <ApproverSearchInput
          positionOptions={canAddPositions ? positionOptions : []}
          pickedRefs={refs}
          onPick={addApprover}
          disabled={disabled}
          sx={{ width: '100%', maxWidth: 360 }}
        />
      </Stack>

      {refs.length === 0 && <EmptyState />}

      {refs.length > 0 && (
        <DraggableList data={refs} onChange={(nextRefs) => emit(nextRefs)}>
          {refs.map((ref, index) => {
            const key = approverRefKey(ref)
            const position =
              ref.kind === 'position'
                ? positionOptionsById.get(ref.positionId)
                : undefined
            const user =
              ref.kind === 'user' ? usersById.get(ref.userId) : undefined

            const isLoading =
              ref.kind === 'user' ? !user && usersPending : positionsPending

            const name =
              ref.kind === 'position'
                ? position?.position.name ?? 'Unknown position'
                : user?.name || 'Unknown user'

            // Until the names land a row has only its id, so labels fall back
            // to the ordinal to stay distinct.
            const label = isLoading ? `approver ${index + 1}` : name

            const email = ref.kind === 'user' ? user?.email : undefined

            return (
              // @ts-expect-error -- Draggable can have children
              <Draggable key={key}>
                <Paper
                  variant="outlined"
                  sx={{ px: 1.5, py: 1, mb: 0.75 }}
                  data-testid={`approver-row-${key}`}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <IconButton
                      className="drag-handle"
                      size="small"
                      disabled={disabled}
                      data-approver-handle={index}
                      aria-label={`Reorder ${label}. Use the up and down arrow keys.`}
                      onKeyDown={(event) => handleKeyDown(event, index)}
                      sx={{ cursor: 'grab', ':active': { cursor: 'grabbing' } }}
                    >
                      <DragIndicator fontSize="inherit" />
                    </IconButton>

                    {showNumbers && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          minWidth: 16,
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {index + 1}
                      </Typography>
                    )}

                    {ref.kind === 'position' ? (
                      <PositionAvatar loading={isLoading} />
                    ) : (
                      <UserAvatar name={user?.name} loading={isLoading} />
                    )}

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      {isLoading ? (
                        <Typography variant="body2">
                          <Skeleton width={140} />
                        </Typography>
                      ) : (
                        <Typography variant="body2" noWrap>
                          {name}
                        </Typography>
                      )}
                      {email && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          component="div"
                          noWrap
                        >
                          {email}
                        </Typography>
                      )}
                    </Box>

                    <IconButton
                      size="small"
                      disabled={disabled}
                      aria-label={`Remove ${label}`}
                      onClick={() => removeApprover(key)}
                    >
                      <Close fontSize="inherit" />
                    </IconButton>
                  </Stack>
                </Paper>
              </Draggable>
            )
          })}
        </DraggableList>
      )}
    </Stack>
  )
}
